import { XGridDefaultColumn } from 'app/components/Common/XGrid/cells/XGridDefaults';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import React, { useEffect, useState } from 'react';
import { XGridEditableCell } from 'app/components/Common/XGrid/cells/XGridEditableCell';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { Education, IEducationInfo } from 'app/models/Candidate/CandidateProfile';
import {
  countryCheck,
  emptyCheck,
  flattenEduData,
  getId,
  getMonthId,
  getVal,
  IEducationInBoundData,
  valCharLimit,
} from './EducationHelper';
import { getCandidateById, postCandidateEducation } from 'app/services/CandidateServices/CandidateServices';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'oidc/user.selectors';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { useParams } from 'react-router-dom';
import { getLocationsByKeyword } from 'app/services/SharedServices/SharedServices';
import statesData from 'app/assets/jsons/State.json';
import { GridAutoCompleteCell } from './EducationCityAutoComplete';
import { candidateDetailActions } from '../../../Profile/CandidateDetails.redux';
import { GridDropdownCell } from './EducationDropdownCell';
import { lookupSelector } from 'store/redux-store/lookup/lookup.selectors';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { XGridIconCell } from 'app/components/Common/XGrid/cells/XGridIconCell';
import { useTranslation } from 'react-i18next';
import { isPositiveNumber } from 'app/helpers/componentHelpers';
import { debounce } from 'lodash';
import { trackException } from 'app-insights/appInsightsTracking';
import { useReadOnly } from 'oidc/userRoles';

interface EducationXGridGridProps {
  rows: Education[];
  degrees?: any[];
  majors?: any[];
}

export const EducationXGrid = ({ rows }: EducationXGridGridProps) => {
  const [sortModel] = React.useState<any>([
    {
      field: 'graduatedOnYear',
      sort: 'desc',
    },
    {
      field: 'graduatedOnMonth',
      sort: 'desc',
    },
    {
      field: 'school',
      sort: 'desc',
    },
  ]);
  const [data, setData] = useState<IEducationInBoundData[]>([]);
  const user = useSelector(selectUser);
  const { t } = useTranslation();
  const { travelerId: candidateId, brandId } = useParams<{ travelerId: string; brandId: string }>();
  const dispatch = useDispatch();
  const [columns, setColumns] = React.useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [updated, setUpdated] = useState(false);
  const selMasterCopy = useSelector(lookupSelector);
  const {readOnly} = useReadOnly();

  React.useEffect(() => {
    setColumns(getDefaults(readOnly));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resetColumns = () => {
    setColumns(getDefaults(readOnly));
  };

  useEffect(() => {
    if (updated) {
      setLoading(false);
      setUpdated(false);
      dispatch(
        globalActions.setSnackBar({
          message: 'Education details have been updated successfully',
          severity: 'success',
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updated]);

  const fetchData = debounce(
    (key, callback: (res: any) => void) => {
      getLocationsByKeyword(key)
        .then(res => {
          callback(res);
        })
        .catch(err => {
          trackException(err);
          callback([]);
        });
    },
    1500,
    { leading: true },
  );

  const getDefaults = (readOnly) => {
    const xGridDefaults = [
      {
        ...XGridDefaultColumn,
        field: 'schoolId',
        disableColumnMenu: true,
        sortable: false,
        hide: true,
      },
      {
        ...XGridDefaultColumn,
        field: 'school',
        headerName: 'School',
        sortable: true,
        disableColumnMenu: false,
        renderCell: XGridEditableCell({
          placeholder: 'Enter school',
          editOnSingleClick: true,
          mandatory: ({ value, params }) => true,
          onEndEdit: ({ value, params }) => {
            if (valCharLimit(value, 100)) {
              params.row[params.field] = value;
              updateRow(params.row);
            }
          },
          validator: ({ value, params }) => {
            return valCharLimit(value, 100);
          },
          externalValidator: ({ value, params }) => {
            return !valCharLimit(value, 100) ? 'Max 100 characters' : null;
          },
          disabled: readOnly,
        }),
      },
      {
        ...XGridDefaultColumn,
        field: 'degree',
        headerName: 'Degree',
        sortable: true,
        disableColumnMenu: false,
        renderCell: GridDropdownCell({
          placeholder: 'Enter degree',
          editOnSingleClick: true,
          mandatory: true,
          onEndEdit: ({ value, params }) => {
            if (!!value) {
              params.row[params.field] = value;
              updateRow(params.row);
            }
          },
          validator: ({ value, params }) => {
            return !!value;
          },
          disabled: readOnly,
        }),
      },
      {
        ...XGridDefaultColumn,
        field: 'major',
        headerName: 'Major',
        sortable: true,
        disableColumnMenu: false,
        renderCell: GridDropdownCell({
          placeholder: 'Enter Major',
          editOnSingleClick: true,
          mandatory: true,
          onEndEdit: ({ value, params }) => {
            if (!!value) {
              params.row[params.field] = value;
              updateRow(params.row);
            }
          },
          validator: ({ value, params }) => {
            return !!value;
          },
          disabled: readOnly,
        }),
      },
      {
        ...XGridDefaultColumn,
        field: 'country',
        headerName: 'Country',
        sortable: true,
        disableColumnMenu: false,
        flex: 0,
        width: 150,
        renderCell: GridDropdownCell({
          placeholder: 'Enter country',
          editOnSingleClick: true,
          mandatory: true,
          onEndEdit: ({ value, params }) => {
            if (!!value) {
              params.row[params.field] = value;
              updateRow(params.row);
            }
          },
          validator: ({ value, params }) => {
            return !!value;
          },
          disabled: readOnly,
        }),
      },
      {
        ...XGridDefaultColumn,
        field: 'city',
        headerName: 'City',
        sortable: true,
        disableColumnMenu: false,
        renderCell: GridAutoCompleteCell({
          placeholder: 'Enter city',
          editOnSingleClick: true,
          mandatory: true,
          onEndEdit: ({ value, params }) => {
            if ((params.row.country !== 'USA' && value.length < 100) || (params.row.country === 'USA' && !!value)) {
              params.row[params.field] = value;
              updateRow(params.row);
            }
          },
          validator: ({ value, params }) => {
            return !!value;
          },
          externalValidator: ({ value, params }) => {
            if (params.row.country !== 'USA') return value.length > 100 ? 'Max 100 Characters' : null;
          },
          autoCompleteOptions: selMasterCopy?.countries,
          fetchData: fetchData,
          disabled: readOnly,
        }),
      },
      {
        ...XGridDefaultColumn,
        field: 'state',
        headerName: 'State',
        sortable: true,
        disableColumnMenu: false,
        flex: 0,
        width: 150,
        renderCell: GridDropdownCell({
          placeholder: 'Enter state',
          editOnSingleClick: true,
          mandatory: true,
          onEndEdit: ({ value, params }) => {
            if (!!value) {
              params.row[params.field] = value;
              updateRow(params.row);
            }
          },
          validator: ({ value, params }) => {
            return !!value;
          },
          disabled:readOnly,
        }),
      },
      {
        ...XGridDefaultColumn,
        field: 'graduatedOnMonth',
        headerName: 'Graduation Month',
        sortable: true,
        disableColumnMenu: false,
        flex: 0,
        width: 180,
        renderCell: GridDropdownCell({
          placeholder: '',
          editOnSingleClick: true,
          mandatory: true,
          onEndEdit: ({ value, params }) => {
            if (!!value) {
              params.row[params.field] = value;
              updateRow(params.row);
            }
          },
          validator: ({ value, params }) => {
            return !!value;
          },
          disabled: readOnly,
        }),
      },
      {
        ...XGridDefaultColumn,
        field: 'graduatedOnYear',
        headerName: 'Graduation Year',
        sortable: true,
        disableColumnMenu: false,
        flex: 0,
        width: 250,
        renderCell: GridDropdownCell({
          placeholder: '',
          editOnSingleClick: true,
          mandatory: true,
          onEndEdit: ({ value, params }) => {
            if (!!value) {
              params.row[params.field] = value;
              updateRow(params.row);
            }
          },
          validator: ({ value, params }) => {
            return !!value;
          },
          disabled: readOnly,
        }),
      },
      {
        ...XGridDefaultColumn,
        flex: 0,
        width: 50,
        sortable: false,
        align: 'center',
        disableColumnMenu: false,
        renderCell: XGridIconCell({
          icon: (
            <DeleteOutlinedIcon
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
            />
          ),
          align: 'center',
          onClick: data => {
            dispatch(
              globalActions.setSnackBar({
                message: t('candidate.deleteRecord'),
                severity: null,
                disableIcon: true,
                actions: [
                  {
                    action: () => deleteEducation(data?.row),
                    closeSnackbar: true,
                    text: t('Yes'),
                  },
                  {
                    closeSnackbar: true,
                    text: t('No'),
                  },
                ],
              }),
            );
          },
          disabled: readOnly,
        }),
      },
    ];
    return xGridDefaults;
  };

  useEffect(() => {
    if (rows && !updated) {
      setData(flattenEduData(rows));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const fetchDetails = async () => {
    if (isPositiveNumber(candidateId) && isPositiveNumber(brandId)) {
      await getCandidateById(candidateId as unknown as number, brandId as unknown as number).then(
        async candidateRes => {
          dispatch(candidateDetailActions.setEducation(candidateRes?.experience?.education));
          setUpdated(true);
        },
      );
    }
  };

  //Save Logic
  const preparePayLoad = () => {
    const educationInfo: IEducationInfo = {
      candidateId: parseInt(candidateId),
      brandId: parseInt(brandId),
      schoolId: 0,
      graduatedOnYear: 0,
      graduatedOnMonth: 0,
      graduatedOn: '',
      school: '',
      majorId: null,
      major: '',
      degreeId: 0,
      degree: '',
      location: {
        address1: null,
        address2: null,
        city: '',
        stateProvinceID: null,
        state: null,
        zip: null,
        country: '',
        countryId: null,
        latitude: null,
        longitude: null,
      },
      currentEmployeeId: user.userInfo?.employeeId || 8410,
      currentEmployeeName: user.userInfo?.firstName + ' ' + user.userInfo?.lastName,
    };

    return educationInfo;
  };

  const updateEducation = async postData => {
    setLoading(true);
    const response = await postCandidateEducation(postData);
    if (response?.status == 200) {
      await fetchDetails();
    } else {
      dispatch(
        globalActions.setSnackBar({
          message: t('common.failedLoad'),
          severity: 'error',
        }),
      );
    }
  };

  const deleteEducation = data => {
    const res = {
      schoolId: data.schoolId,
      school: data.school,
      major: data.major,
      degree: data.degree,
      country: data.country,
      city: data.city,
      state: data.state,
      graduationMonth: data.graduatedOnMonth,
      graduationYear: data.graduatedOnYear,
    };
    dispatch(candidateDetailActions.deleteEducationRecord({ requestBody: res }));
  };

  const getStateDesc = val => {
    return !isNaN(val)
      ? selMasterCopy?.states?.find(d => d.StateNumber === val)?.Description
      : selMasterCopy?.states?.find(d => d.ID === val)?.Description;
  };

  const stateCheck = row => {
    return !!row?.stateProvinceID
      ? getStateDesc(row?.stateProvinceID)
      : row?.state && row?.state.length === 2
      ? row?.state || null
      : getId(statesData.states, row?.state) || null;
  };

  const updateRow = (row: IEducationInBoundData) => {
    const educationInfo = preparePayLoad();
    educationInfo.location.city = !emptyCheck(row?.city) ? getVal(row?.city) : null;
    educationInfo.location.country = !emptyCheck(row?.country) ? getVal(row?.country) : null;
    educationInfo.location.countryId = !emptyCheck(row?.country)
      ? parseInt(getId(selMasterCopy?.countries, row?.country))
      : null;
    educationInfo.degree = !emptyCheck(row?.degree) ? getVal(row?.degree) : null;
    educationInfo.degreeId = !emptyCheck(row?.degree) ? parseInt(getId(selMasterCopy?.degrees, row?.degree)) : null;
    educationInfo.graduatedOn = getVal(row?.graduatedOn);
    educationInfo.graduatedOnMonth = getMonthId(row?.graduatedOnMonth);
    educationInfo.graduatedOnYear = !emptyCheck(row?.graduatedOnYear) ? parseInt(getVal(row?.graduatedOnYear)) : null;
    educationInfo.major = !emptyCheck(row?.major) ? getVal(row?.major) : null;
    educationInfo.majorId = !emptyCheck(row?.major) ? parseInt(getId(selMasterCopy?.majors, row?.major)) : null;
    educationInfo.school = !emptyCheck(row?.school) ? getVal(row?.school) : null;
    educationInfo.schoolId = !emptyCheck(row?.school) ? parseInt(getVal(row?.schoolId)) : null;
    educationInfo.location.state = countryCheck(row?.country) ? stateCheck(row) : getVal(row?.state);
    educationInfo.location.stateProvinceID = null;
    educationInfo.location.zip = !emptyCheck(row?.zip) ? getVal(row?.zip) : null;
    educationInfo.location.address1 = rows.find(d => d.schoolId === getVal(row?.schoolId))?.location.address1;

    updateEducation(educationInfo);
  };

  const EduXGrid = React.useMemo(() => {
    return (
      <>
        {data && (
          <XGrid
            toolbarProps={{
              resetColumns: () => resetColumns(),
            }}
            title={''}
            columns={columns}
            rows={data || []}
            loading={loading}
            sortModel={sortModel}
            disableSelectionOnClick
          />
        )}
      </>
    );
  }, [columns, loading, data, resetColumns, sortModel]);

  return (
    <>
      <LayoutGrid>
        <LayoutGridItem xs={12}>{EduXGrid}</LayoutGridItem>
      </LayoutGrid>
    </>
  );
};
